import { translateArgumentErrors } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import get from 'lodash/get';
import {
    SURVEY_ERROR_SPEC,
    translateDynamicSurveyErrors,
} from '../../utils/surveys';
import { FetchV3Options, fetchV3WithTranslateServerErrors } from '../base';
import {
    BEST_AVAILABLE_NOT_FOUND_ERROR,
    PAYEE_ACCOUNT_ERROR,
    PAYPAL_PERMISSION_ERROR,
} from '../constants';

const virtualIncentivesErrorText = gettext(
    'We cannot use your credits at this time. Please use a different payment method to complete your order.',
);

const ORDER_ERROR_SPEC = {
    default: () =>
        gettext('There was a problem submitting your order. Please try again.'),

    // We receive a missing order user error when we have trouble either getting a logged-in user from the request
    // or creating a user with a new email address. Usually that means the email address is missing
    MISSING_ORDER_USER: () =>
        gettext(
            'There was a problem with your email address. Please review your basic info',
        ),

    INVALID_PAYMENT_REQUEST: () =>
        gettext(
            'There were errors with your payment. Please review your payment details',
        ),
    INVENTORY_ERROR: (parsedError?: {
        description?: { ERROR_NO_MATCH_FOUND?: string } | string;
    }) => {
        if (
            get(parsedError, 'description.ERROR_NO_MATCH_FOUND') ===
            BEST_AVAILABLE_NOT_FOUND_ERROR
        ) {
            return BEST_AVAILABLE_NOT_FOUND_ERROR;
        }

        if (
            get(parsedError, 'description') ===
            'InventoryServiceException: Discount requested is not available'
        ) {
            return gettext(
                'The discount you selected is no longer available. To continue, please remove it and choose a ticket to complete your purchase.',
            );
        }

        return gettext('The tickets you selected are no longer available');
    },
    UNITS_NOT_AVAILABLE_PROMO_CODE: () =>
        gettext(
            'Your requested ticket quantity exceeds the number provided by your promotional code.',
        ),
    UNKNOWN_INVENTORY_ERROR: () =>
        gettext('There was a problem submitting your order. Code 17'),
    ORDER_EXPIRED: () => gettext('Your order has expired'),
    REDIRECT_PAYMENT_REQUEST: () => 'REDIRECTED',
    DECLINED: () => gettext('Your payment was declined'),
    PAYMENT_DECLINED: () => gettext('Your payment was declined'),
    ARGUMENTS_ERROR: translateArgumentErrors({
        default: translateDynamicSurveyErrors(SURVEY_ERROR_SPEC, false),
    }),
    TICKETS_CONSTRAINTS_MUTUALLY_EXCLUDED: () =>
        gettext('Please select tickets with the same payment method.'),
    TEAM_INVALID_CAPACITY: () =>
        gettext(
            "The group you are registering for doesn't have enough available spaces.",
        ),
    ERROR_ATTENDEE_CREDIT_PAYMENT_FAILED: () => virtualIncentivesErrorText,
    ATTENDEE_CREDIT_PAYMENT_FAILED: () => virtualIncentivesErrorText,
    NOT_ENOUGH_VI_BALANCE: () => virtualIncentivesErrorText,
    MALFORMED_SINGLE_PAYMENT_METHOD: () =>
        gettext(
            'The price of your order has changed. Please start a new order to get the correct price',
        ),
    EVENT_NOT_FOUND_OR_NOT_ALLOWED: () =>
        gettext(
            "We can't complete your order because this event hasn't been published yet",
        ),
    PAYMENT_FAILED: (parsedError?: { description?: string }) => {
        if (
            get(parsedError, 'description') === PAYEE_ACCOUNT_ERROR ||
            get(parsedError, 'description') === PAYPAL_PERMISSION_ERROR
        ) {
            return gettext(
                "We're having trouble connecting to PayPal. Please try again later.",
            );
        }

        return gettext(
            'There was a problem submitting your order. Please try again.',
        );
    },
    ACCOUNT_BLOCKED: () =>
        gettext(
            'This email address can’t be used to register for events at the moment. Please use a different email address or contact trust@eventbrite.com to verify ownership of the email address.',
        ),
    INVALID_WAITLIST_REQUEST: () =>
        gettext(
            'Your spot has expired. Join waitlist again for another opportunity.',
        ),
    TOO_MANY_TICKETS: () =>
        gettext('Order has too many items. Max items is 100'),
    WAITING_ROOM_ENABLED: () =>
        gettext(
            'There was an issue submitting your request due to high demand for this event, please reload the page to register.',
        ),
};

const ORDER_SURVEY_ERROR_SPEC = {
    default: () =>
        gettext('There was a problem submitting your form. Please try again.'),

    ARGUMENTS_ERROR: translateArgumentErrors({
        default: translateDynamicSurveyErrors(SURVEY_ERROR_SPEC, true),
    }),
};

export const _fetchV3WithOrderErrorHandling = (
    url: string,
    options: FetchV3Options = {},
    onCatchError?: () => {},
) =>
    fetchV3WithTranslateServerErrors(
        ORDER_ERROR_SPEC,
        url,
        options,
        onCatchError,
    );

export const _fetchV3WithOrderSurveyErrorHandling = (
    url: string,
    options: FetchV3Options = {},
) => fetchV3WithTranslateServerErrors(ORDER_SURVEY_ERROR_SPEC, url, options);
